import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Cog,
  Calendar,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Hash,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerInstallations = () => {
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInstallations();
  }, []);

  const fetchInstallations = async () => {
    try {
      const response = await axios.get(`${API}/my/installations`, { withCredentials: true });
      setInstallations(response.data);
    } catch (error) {
      console.error("Error fetching installations:", error);
      toast.error("Failed to load installations");
    } finally {
      setLoading(false);
    }
  };

  const getServiceStatus = (installation) => {
    if (!installation.next_service_due) {
      return { label: "No Service Data", color: "bg-slate-100 text-slate-600", icon: Clock };
    }
    const today = new Date();
    const dueDate = new Date(installation.next_service_due);
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return { label: "Overdue", color: "bg-red-100 text-red-700", icon: AlertTriangle };
    }
    if (daysUntilDue <= 14) {
      return { label: "Due Soon", color: "bg-amber-100 text-amber-700", icon: Clock };
    }
    return { label: "Up to Date", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 };
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-slate-200 rounded-sm"></div>
            <div className="h-64 bg-slate-200 rounded-sm"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="customer-installations-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
          My Installations
        </h1>
        <p className="text-slate-500 mt-1">
          View details of your installed systems and service status
        </p>
      </div>

      {installations.length === 0 ? (
        <Card className="rounded-sm border-slate-200">
          <CardContent className="py-12 text-center">
            <Cog className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No Installations Found
            </h3>
            <p className="text-slate-500">
              You don&apos;t have any installations registered yet. Please contact us to
              add your installations.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {installations.map((installation) => {
            const status = getServiceStatus(installation);
            const StatusIcon = status.icon;

            return (
              <Card
                key={installation.installation_id}
                className="rounded-sm border-slate-200"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-indigo-100 rounded-sm flex items-center justify-center">
                        <Cog className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {installation.system_name}
                        </CardTitle>
                        {installation.location_name && (
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <MapPin className="h-3 w-3" />
                            {installation.location_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge className={`rounded-sm ${status.color}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {status.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Installation Details Grid */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                      <p className="text-slate-500 flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        Installed
                      </p>
                      <p className="font-medium">
                        {installation.installation_date || "Not recorded"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-slate-500 flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Last Service
                      </p>
                      <p className="font-medium">
                        {installation.last_service_date || "Not recorded"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-slate-500 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        Next Service Due
                      </p>
                      <p className={`font-medium ${
                        status.label === "Overdue" ? "text-red-600" :
                        status.label === "Due Soon" ? "text-amber-600" : ""
                      }`}>
                        {installation.next_service_due || "Not scheduled"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-slate-500 flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        Service Interval
                      </p>
                      <p className="font-medium">
                        {installation.service_interval_days
                          ? `Every ${installation.service_interval_days} days`
                          : "Not set"}
                      </p>
                    </div>
                  </div>

                  {/* Serial Number */}
                  {installation.serial_number && (
                    <div className="pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-2 text-sm">
                        <Hash className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-500">Serial Number:</span>
                        <span className="font-mono text-slate-700">
                          {installation.serial_number}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {installation.notes && (
                    <div className="pt-3 border-t border-slate-100">
                      <div className="flex items-start gap-2 text-sm">
                        <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                        <div>
                          <span className="text-slate-500">Notes:</span>
                          <p className="text-slate-700 mt-1">{installation.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerInstallations;
